import { useState } from "react";
import { Box, ButtonBase, Popover, Tooltip, Typography } from "@mui/material";
import { OFFER_ICONS, offerIconKey, offerIconLabel } from "@shared/offerIcons";
import { tokens } from "../../theme";
import OfferIcon from "./OfferIcon";

interface Props {
  /** The stored key, or anything else — unknown values draw the fallback. */
  value: string | undefined;
  onChange: (next: string) => void;
  /** Names the row in the aria-label, e.g. "Handdukar". */
  rowLabel: string;
  testId?: string;
}

/**
 * Pick one glyph from the shared catalogue.
 *
 * A popover of tiles rather than a `<Select>` of words: the operator is
 * choosing a PICTURE, and a dropdown listing "towels / bedding / toiletries"
 * makes them translate a word back into the thing the guest will see, once per
 * row. The tile they click is exactly what the television draws — same
 * component, same geometry — so there is nothing to be surprised by later.
 *
 * Both labels are shown under each glyph. The demo property is Swedish and the
 * CMS chrome is English, and an operator scanning sixteen pictures should not
 * have to pick which language they are thinking in first.
 */
export default function OfferIconPicker({
  value,
  onChange,
  rowLabel,
  testId,
}: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = offerIconKey(value);
  const name = rowLabel.trim() || "this item";

  return (
    <>
      <Tooltip title={`Icon: ${offerIconLabel(current)}`}>
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={`Icon for ${name}`}
          data-testid={testId}
          sx={{
            width: 44,
            height: 44,
            flex: "none",
            borderRadius: "10px",
            border: "1px solid",
            borderColor: tokens.divider,
            bgcolor: tokens.rowAlt,
            color: tokens.ink,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": { borderColor: tokens.brand },
          }}
        >
          <OfferIcon name={current} size={24} />
        </ButtonBase>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          data-testid={testId ? `${testId}-grid` : undefined}
          role="radiogroup"
          aria-label={`Icon for ${name}`}
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 84px)",
            gap: 0.5,
            p: 1,
          }}
        >
          {OFFER_ICONS.map((def) => {
            const on = def.key === current;
            return (
              <ButtonBase
                key={def.key}
                role="radio"
                aria-checked={on}
                aria-label={`${def.label.en} · ${def.label.sv}`}
                data-testid={testId ? `${testId}-${def.key}` : undefined}
                onClick={() => {
                  onChange(def.key);
                  setAnchor(null);
                }}
                sx={{
                  flexDirection: "column",
                  gap: 0.5,
                  py: 1,
                  px: 0.5,
                  borderRadius: "10px",
                  border: "1px solid",
                  borderColor: on ? tokens.brand : "transparent",
                  bgcolor: on ? tokens.brandTintSoft : "transparent",
                  color: tokens.ink,
                  "&:hover": { bgcolor: tokens.rowAlt },
                }}
              >
                <OfferIcon name={def.key} size={26} />
                <Typography
                  sx={{
                    fontSize: 11,
                    lineHeight: 1.2,
                    textAlign: "center",
                    color: tokens.inkMuted,
                  }}
                >
                  {def.label.en}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    lineHeight: 1.2,
                    textAlign: "center",
                    color: tokens.inkFaint,
                  }}
                >
                  {def.label.sv}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Popover>
    </>
  );
}
